// src/App.tsx
import React, { useEffect, useState } from "react";
import { CookiesProvider, useCookies } from "react-cookie";
import { RootState } from "src/store/app/store";
import i18n from "src/locales/i18n";
import { useAppDispatch, useAppSelector } from "src/store/app/hooks";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Router from "./router/router";
import { getSetting } from "src/store/features/settingSlice";
import { setLocale } from "src/store/features/settingSlice";
import Splash from "src/pages/Splash";
import { Box, createTheme, Paper, Stack, styled, ThemeProvider, Typography, useMediaQuery } from "@mui/material";
import { getUserInfoAPI } from "./store/features/user/authInfoSlice";
import LoginPage from "./pages/LoginPage";
import { loginCheckAPI } from "./store/features/user/login/loginCheckSlice";
import { getUserMyCreditBalanceAPI } from "./store/features/user/credit/userMyCreditBalanceSlice";
import { useTranslation } from "react-i18next";
import fetchCsrfToken, {
  checkSessionStatus,
  setSessionExpiredCallback,
} from "./store/features/auth/csrfTokenSlice";
import { getUserMyActivityHistoryAPI } from "./store/features/user/userActivitySlice";
import Background from "./assets/images/background/background.svg";
import Logo from "./assets/images/drugvlab_logo_light_mode.svg";

const CreateAccountPage = React.lazy(() => import("./pages/CreateAccountPage"));
const QuickActionLauncher = React.lazy(() => import("./pages/QuickActionLauncher"));
const VerifyEmailPage = React.lazy(() => import("./pages/VerifyEmailPage"));
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
const PasswordResetVerifyPage = React.lazy(() => import("./pages/PasswordResetVerifyPage"));
const ForgotPasswordPage = React.lazy(() => import("./pages/ForgotPasswordPage"));

// 모바일 뷰포트에서는 앱 사용을 막고 데스크톱 접속 안내 화면을 보여줌
const MobileBlockScreen = () => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        backgroundImage: `linear-gradient(180deg, rgba(4, 18, 45, 0.82), rgba(4, 18, 45, 0.72)), url(${Background})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 360,
          p: 4,
          borderRadius: 4,
          textAlign: "center",
          backgroundColor: "rgba(255,255,255,0.96)",
          boxShadow: "0 20px 50px rgba(15, 23, 42, 0.24)",
        }}
      >
        <Stack spacing={2.5} alignItems="center">
          <Box
            component="img"
            src={Logo}
            alt="DrugVLab Logo"
            sx={{ width: 160, maxWidth: "100%" }}
          />
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#e2e8f0",
              fontSize: 28,
            }}
          >
            💻
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
            {t("app.mobile_block.title")}
          </Typography>
          <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.9 }}>
            {t("app.mobile_block.body_1")}
            <br />
            {t("app.mobile_block.body_2")}
            <br />
            {t("app.mobile_block.body_3")}
            <br />
            {t("app.mobile_block.body_4")}
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};


// Main App 함수
function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // useState 상태 가져오기
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Mobile 화면의 경우 , 너비 값 지정
  const isMobileViewport = useMediaQuery("(max-width: 767px)");
  const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";

  // 백엔드 API 형식으로 들어온 비밀번호 재설정 링크를 프론트 라우트로 보정해주기 위해
  const isApiPasswordResetConfirmRoute = normalizedPath.startsWith("/api/auth/password-reset/confirm");
  const resetRidOrSid =
    new URLSearchParams(location.search).get("rid") ||
    new URLSearchParams(location.search).get("sid") ||
    "";
  const resetToken = new URLSearchParams(location.search).get("token") || "";

  // 로그인 전에도 접근 가능한 인증/계정 관련 공개 라우트 목록
  const isPublicAuthRoute =
    normalizedPath === "/create-account" ||
    normalizedPath === "/forgot-password" ||
    normalizedPath === "/verify-email" ||
    normalizedPath.startsWith("/email-verification/") ||
    normalizedPath.startsWith("/auth/password-reset/confirm") ||
    normalizedPath.startsWith("/auth/password-reset/complete") ||
    normalizedPath.startsWith("/password-reset/success") || 
    normalizedPath.startsWith("/api/auth/password-reset/confirm");

  // API 비밀번호 재설정 URL을 사용자가 볼 수 있는 프론트 라우트로 즉시 리다이렉트
  useEffect(() => {
    if (!isApiPasswordResetConfirmRoute) {
      return;
    }

    const params = new URLSearchParams();
    if (resetRidOrSid) params.set("rid", resetRidOrSid);
    if (resetToken) params.set("token", resetToken);
    const query = params.toString();
    window.location.href = query
      ? `/auth/password-reset/confirm?${query}`
      : "/auth/password-reset/confirm";
  }, [isApiPasswordResetConfirmRoute, resetRidOrSid, resetToken]);

  // 자동 로그인 저장값은 로그아웃/세션 만료 시 인증 상태와 함께 제거
  const clearAutoLoginData = () => {
    localStorage.removeItem("autoLoginData");
  };

  // 401 에러 감지 시 로그아웃 처리
  useEffect(() => {
    const handleUnauthorized = () => {
      if (isPublicAuthRoute) {
        return;
      }
      setIsAuthenticated(false);
      navigate("/login", { replace: true });
    };
    window.addEventListener("unauthorized", handleUnauthorized);
    return () => window.removeEventListener("unauthorized", handleUnauthorized);
  }, [isPublicAuthRoute, navigate]);

  // 뒤로가기/앞으로가기 감지 — 인증 상태 기반으로 /login 차단
  useEffect(() => {
    if (isPublicAuthRoute) {
      return;
    }

    // 인증 확정 시, /dashboard url 로 navigate
    const handlePopState = () => {
      if (isAuthenticated && window.location.pathname === "/login") {
        navigate("/dashboard", { replace: true });
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isAuthenticated, isPublicAuthRoute, navigate]);

  // 로그아웃 함수
  const handleLogout = () => {
    clearAutoLoginData();
    setIsAuthenticated(false);
    navigate("/login", { replace: true });
  };

  // 세션 만료 콜백 설정
  useEffect(() => {
    if (isPublicAuthRoute) {
      return;
    }

    setSessionExpiredCallback(() => {
      setIsAuthenticated(false);
      navigate("/login", { replace: true });
    });
  }, [isPublicAuthRoute, navigate]);

  // CSRF 토큰 초기화 및 세션 검증
  useEffect(() => {
    // 공개 인증 페이지에서는 보호 API 호출이 필요 없으므로 초기화
    if (isPublicAuthRoute) {
      setIsInitializing(false);
      return;
    }

    const initializeTokenAndSession = async () => {
      try {
        // CSRF 토큰 발급 후 서버 세션이 살아 있는지 확인
        const token = await fetchCsrfToken();
        if (!token) { handleLogout(); return; }
        const sessionValid = await checkSessionStatus();
        if (!sessionValid) { handleLogout(); return; }
      } catch (error) {
        console.error("CSRF 토큰 초기화 실패:", error);
      }
    };
    initializeTokenAndSession();
  }, [isPublicAuthRoute]);

  // 초기 인증 상태 확인
  useEffect(() => {
    // 공개 인증 페이지는 로그인 체크를 기다리지 않고 바로 렌더링
    if (isPublicAuthRoute) {
      setIsInitializing(false);
      return;
    }

    const initializeApp = async () => {
      try {
        // 앱 설정을 먼저 불러온 뒤 현재 세션의 로그인 상태를 확인
        dispatch(getSetting());
        const loginCheckResult = await dispatch(loginCheckAPI()).unwrap();

        if (loginCheckResult?.is_authenticated) {
          setIsAuthenticated(true);
          try {
            // 인증된 사용자에게 필요한 공통 데이터를 앱 시작 시 함께 적재
            await dispatch(getUserInfoAPI()).unwrap();
            await dispatch(getUserMyActivityHistoryAPI()).unwrap();
            await dispatch(getUserMyCreditBalanceAPI()).unwrap();
          } catch (userInfoError) {
            console.error("사용자 정보 가져오기 실패:", userInfoError);
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("앱 초기화 중 오류:", error);
        setIsAuthenticated(false);
        if (error?.response?.status === 500) clearAutoLoginData();
      } finally {
        setIsInitializing(false);
      }
    };
    // App 초기화 함수 호출
    initializeApp();
  }, [dispatch, isPublicAuthRoute]);

  // 내부 로그아웃 액션
  const handleSignOut = async () => {
    try {
      clearAutoLoginData();
      setIsAuthenticated(false);
    } catch (error) {
      console.error("로그아웃 중 오류: ", error);
      clearAutoLoginData();
      setIsAuthenticated(false);
    }
  };

  if (isInitializing) return <Splash />;

  // API 리다이렉트 처리 중에는 빈 화면 대신 Splash 화면 return
  if (isApiPasswordResetConfirmRoute) return <Splash />;

  // 공개 인증 라우트는 보호 라우터를 거치지 않고 별도 Routes로 렌더링
  if (isPublicAuthRoute) {
    return (
      <React.Suspense fallback={<Splash />}>
        <Routes>
        <Route path="/email-verification/success" element={<VerifyEmailPage />} />
        <Route path="/email-verification/success/" element={<VerifyEmailPage />} />
        <Route path="/email-verification/fail" element={<VerifyEmailPage />} />
        <Route path="/email-verification/fail/" element={<VerifyEmailPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/verify-email/" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/forgot-password/" element={<ForgotPasswordPage />} />
        <Route path="/auth/password-reset/confirm" element={<PasswordResetVerifyPage />} />
        <Route path="/auth/password-reset/confirm/" element={<PasswordResetVerifyPage />} />
        <Route path="/auth/password-reset/complete" element={<ResetPasswordPage />} />
        <Route path="/auth/password-reset/complete/" element={<ResetPasswordPage />} />
        <Route path="/api/auth/password-reset/confirm" element={<PasswordResetVerifyPage />} />
        <Route path="/api/auth/password-reset/confirm/" element={<PasswordResetVerifyPage />} />
        <Route
          path="/create-account"
          element={<CreateAccountPage onLoginSuccess={() => setIsAuthenticated(true)} />}
        />
        <Route
          path="*"
          element={
            <LoginPage
              onLoginSuccess={() => {
                setIsAuthenticated(true);
                navigate("/dashboard", { replace: true }); // 히스토리에 /login 안 남김
              }}
            />
          }
        />
      </Routes>
      </React.Suspense>
    );
  }

  // 보호 라우트 접근 중 인증되지 않았으면 Login Page 출력
  if (!isAuthenticated) {
    return (
      <React.Suspense fallback={<Splash />}>
        <Routes>
        <Route
          path="*"
          element={
            <LoginPage
              onLoginSuccess={() => {
                setIsAuthenticated(true);
                navigate("/dashboard", { replace: true }); // 히스토리에 /login 안 남김
              }}
            />
          }
        />
      </Routes>
      </React.Suspense>
    );
  }

  // 인증된 사용자라도 모바일 화면에서는 워크플로우 UI 대신 차단 안내를 보여줌
  if (isMobileViewport) {
    return <MobileBlockScreen />;
  }

  return (
    <CookiesProvider>
      <MainApp onSignOut={handleSignOut} />
    </CookiesProvider>
  );
}

// drawer 너비만큼 본문 영역을 밀어 사이드바와 라우트 콘텐츠가 겹치지 않게 함
const StyledMain = styled("main", {
  shouldForwardProp: (prop) => prop !== "drawerWidth",
})<{ drawerWidth?: number }>(({ theme, drawerWidth = 240 }) => ({
  flexGrow: 1,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  backgroundColor:
    theme.palette.mode === "dark" ? theme.palette.background.default : "#fff",
  marginLeft: `${drawerWidth}px`,
}));

// Router에 전달되는 레이아웃 래퍼로, Redux의 drawer 너비 상태를 본문 margin에 반영
function MainWrapper({ children }: { children?: React.ReactNode }) {
  const drawerWidth = useAppSelector((state: RootState) => state.drawer.drawerWidth);
  return (
    <StyledMain drawerWidth={drawerWidth}>
      {children}
    </StyledMain>
  );
}

function MainApp({ onSignOut }: { onSignOut: () => void }) {
  const dispatch = useAppDispatch();

  // useState 상태
  const [cookies] = useCookies(["drugvlab-settings"]);
  const [showSplashScreen, setShowSplashScreen] = useState(true);
  const [launcherDismissed, setLauncherDismissed] = useState(false);

  // Redux에서 값 가져오기
  const userFirstLogin = useAppSelector((state) => state?.loginCheck?.response?.is_first_login);

  // 앱 전역에서 사용하는 MUI 기본 테마를 이곳에서 한 번 생성
  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          contrastThreshold: 3,
          mode: "light",
          primary: {
            light: "#ffffff",
            main: "#efefef",
            dark: "#dfdfdf",
            contrastText: "#000",
          },
          secondary: {
            light: "#a6bfdf",
            main: "#5b74b8",
            dark: "#4056c9",
            contrastText: "#fff",
          },
        },
      }),
    []
  );

  const savedLocale = cookies["drugvlab-settings"]?.locale;

  useEffect(() => {
    // 저장된 언어 설정을 적용한 직후 앱을 표시해 고정 Splash 지연을 만들지 않음.
    const path = window.location.pathname;
    const isAuthEntryRoute =
      path === "/login" ||
      path === "/create-account" ||
      path === "/forgot-password" ||
      path.startsWith("/email-verification") ||
      path.startsWith("/verify-email");
    const locale = isAuthEntryRoute ? "en" : savedLocale ?? "en";
    dispatch(setLocale(locale));
    void i18n.changeLanguage(locale);
    setShowSplashScreen(false);
  }, [dispatch, savedLocale]);

  if (showSplashScreen) return <Splash />;

  // 첫 로그인 사용자는 대시보드 대신 빠른 시작 런처를 먼저 보여줌
  if (userFirstLogin && !launcherDismissed) {
    return (
      <React.Suspense fallback={<Splash />}>
        <QuickActionLauncher
          onWorkspaceStarted={() => setLauncherDismissed(true)}
          onClose={() => setLauncherDismissed(true)}
        />
      </React.Suspense>
    );
  }

  // 인증 완료 후 실제 앱 라우터와 전역 테마를 연결
  return (
    <ThemeProvider theme={theme}>
      <Router Main={MainWrapper} />
    </ThemeProvider>
  );
}

export default App;
